import React, { useCallback, useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Fancybox } from '@fancyapps/ui';
import '@fancyapps/ui/dist/fancybox/fancybox.css';
import type { PhotoModel } from '../../../../types/protocols';
import { FirebaseImage, ImageSize, resolveFirebaseImageUrl } from '../../../../Helpers/ImageLoader';
import { useUsersData } from '../../../../Helpers/UsersContext';
import { buildPhotoCaption } from '../../../../Helpers/photoCaptionUtils';
import { LocationThumbnail, LocationModal, LocationData } from '../../../../Components/LocationDisplay';
import { useInView } from '../../../../hooks/useInView';
import type { ComponentMedia } from '../utils/componentMedia';

/** Thumbnails shown before the rest go behind the chevron */
export const COLLAPSED_COUNT = 3;

interface ComponentListMediaProps {
    /** The component's pictures in display order; the first is the primary thumbnail */
    media: ComponentMedia[];
    expanded: boolean;
    onToggleExpanded: () => void;
}

/** One of the component's own photos as a tile; opens the component's photos in Fancybox */
const PhotoTile: React.FC<{ photo: PhotoModel; onClick: () => void }> = ({ photo, onClick }) => (
    <div className="component-list-media-photo" onClick={(e) => { e.stopPropagation(); onClick(); }} title="Foto">
        <FirebaseImage storagePath={photo.storagePath} size={ImageSize.THUMBNAIL_SMALL} alt="Foto" />
    </div>
);

/**
 * The second row of a list item in Massredigering: where the component is and
 * what it looks like. Collapsed it shows up to three thumbnails and a chevron
 * tile when there is more; expanded, every position and photo. Clicks on a
 * thumbnail or the chevron stop here; any other click bubbles to the row, which
 * toggles the expansion. Positions open
 * LocationModal, photos the Fancybox gallery of the component's own photos.
 * Thumbnails mount only once the row is near the viewport.
 */
export const ComponentListMedia: React.FC<ComponentListMediaProps> = ({ media, expanded, onToggleExpanded }) => {
    const { ref, inView } = useInView<HTMLDivElement>();
    const [openLocation, setOpenLocation] = useState<LocationData | null>(null);
    const { users } = useUsersData();

    const photos = useMemo(() => media.flatMap(m => (m.kind === 'photo' ? [m.photo] : [])), [media]);

    const openPhoto = useCallback(async (photo: PhotoModel) => {
        // Resolve every URL first, as PhotoGallery does, so no slide opens on a placeholder
        const urls = await Promise.all(photos.map(p => resolveFirebaseImageUrl(p.storagePath).catch(() => p.storagePath)));
        Fancybox.show(
            photos.map((p, i) => ({ src: urls[i], caption: buildPhotoCaption(p, users) })),
            { animated: true, showClass: 'fancybox-fadeIn', startIndex: Math.max(0, photos.indexOf(photo)) }
        );
    }, [photos, users]);

    if (media.length === 0) return null;

    const shown = expanded ? media : media.slice(0, COLLAPSED_COUNT);
    const canExpand = media.length > COLLAPSED_COUNT;

    return (
        <div
            ref={ref}
            className={`component-list-item-media ${expanded ? 'expanded' : ''}`}
        >
            <div className="component-list-media-tiles">
                {inView && shown.map(item => item.kind === 'position' ? (
                    <LocationThumbnail
                        key={item.key}
                        location={item.location}
                        onClick={(e) => { e.stopPropagation(); setOpenLocation(item.location); }}
                    />
                ) : (
                    <PhotoTile key={item.key} photo={item.photo} onClick={() => openPhoto(item.photo)} />
                ))}
            </div>
            {canExpand && (
                <button
                    type="button"
                    className="component-list-media-toggle"
                    onClick={(e) => { e.stopPropagation(); onToggleExpanded(); }}
                    aria-expanded={expanded}
                    aria-label={expanded ? 'Visa färre bilder' : `Visa alla ${media.length} bilder`}
                    title={expanded ? 'Visa färre' : `Visa alla ${media.length}`}
                >
                    {!expanded && <span className="component-list-media-more">+{media.length - COLLAPSED_COUNT}</span>}
                    <ChevronDown size={20} className={`component-list-media-chevron ${expanded ? 'expanded' : ''}`} />
                </button>
            )}
            {/* The modal is a portal, so its React clicks would still bubble to the row and toggle it */}
            {openLocation && (
                <div onClick={(e) => e.stopPropagation()}>
                    <LocationModal location={openLocation} onClose={() => setOpenLocation(null)} />
                </div>
            )}
        </div>
    );
};

export default ComponentListMedia;
